import React, { useState } from "react";
import { Input, Select, Button, DatePicker } from "antd";
import { FilterOutlined } from "@ant-design/icons";

const { RangePicker } = DatePicker;

interface FilterField {
  key: string;
  label: string;
  type: "text" | "select" | "date" | "dateRange";
  options?: { value: string | number; label: string }[];
  loading?: boolean;
}

interface TableFilterProps {
  fields: FilterField[];
  onApplyFilters: (filters: Record<string, any>) => void;
  onClearFilters: () => void;
}

const TableFilter: React.FC<TableFilterProps> = ({
  fields,
  onApplyFilters,
  onClearFilters,
}) => {
  const [filters, setFilters] = useState<Record<string, any>>({});

  const handleFilterChange = (key: string, value: any) => {
    if (value === undefined || value === "") {
      // Remove the filter if it's empty
      const newFilters = { ...filters };
      delete newFilters[key];
      setFilters(newFilters);
    } else {
      setFilters((prev) => ({ ...prev, [key]: value }));
    }
  };

  const handleClearFilters = () => {
    setFilters({});
    onClearFilters();
  };

  return (
    <div className="bg-white p-4 shadow-md rounded-lg space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {fields.map(({ key, label, type, options, loading }) => (
          <div key={key}>
            {type === "text" && (
              <Input
                placeholder={label}
                value={filters[key] || ""}
                onChange={(e) => handleFilterChange(key, e.target.value)}
                allowClear
              />
            )}
            {type === "select" && (
              <Select
                placeholder={label}
                value={filters[key] || undefined}
                onChange={(value) => handleFilterChange(key, value)}
                onClear={() => handleFilterChange(key, undefined)}
                className="w-full"
                options={options}
                loading={loading}
                allowClear
              />
            )}
            {type === "date" && (
              <DatePicker
                placeholder={label}
                value={filters[key]}
                onChange={(date) => handleFilterChange(key, date)}
                className="w-full"
                allowClear
              />
            )}
            {type === "dateRange" && (
              <RangePicker
                placeholder={["Start Date", "End Date"]}
                value={filters[key]}
                onChange={(dates) => handleFilterChange(key, dates)}
                className="w-full"
                allowClear
              />
            )}
          </div>
        ))}
      </div>

      <div className="flex justify-end space-x-3">
        <Button
          type="primary"
          icon={<FilterOutlined />}
          onClick={() => onApplyFilters(filters)}
        >
          Apply Filters
        </Button>
        <Button onClick={handleClearFilters}>Clear Filters</Button>
      </div>
    </div>
  );
};

export default TableFilter;
